import React, { useMemo, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, ComposedChart } from 'recharts';
import { Settings } from 'lucide-react';
interface PortfolioChartProps {
  accountType: 'main' | 'futures' | 'inverse-futures' | 'earn';
}
// Mock chart data
const generateChartData = (accountType: string) => {
  const baseData = {
    main: {
      '1W': Array.from({
        length: 7
      }, (_, i) => ({
        time: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
        value: 4.5 + Math.random() * 0.5,
        upnl: -0.1 + Math.random() * 0.3,
        timestamp: `Day ${i + 1}`
      })),
      '1M': Array.from({
        length: 30
      }, (_, i) => ({
        time: `Day ${i + 1}`,
        value: 4.3 + Math.random() * 0.8,
        upnl: -0.15 + Math.random() * 0.4,
        timestamp: `Day ${i + 1}`
      })),
      '3M': Array.from({
        length: 12
      }, (_, i) => ({
        time: `Week ${i + 1}`,
        value: 4.0 + Math.random() * 1.2,
        upnl: -0.2 + Math.random() * 0.5,
        timestamp: `Week ${i + 1}`
      })),
      '6M': Array.from({
        length: 24
      }, (_, i) => ({
        time: `Week ${i + 1}`,
        value: 3.5 + Math.random() * 1.8,
        upnl: -0.3 + Math.random() * 0.7,
        timestamp: `Week ${i + 1}`
      })),
      '1Y': Array.from({
        length: 12
      }, (_, i) => ({
        time: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i],
        value: 3.0 + Math.random() * 2.5,
        upnl: -0.4 + Math.random() * 1.0,
        timestamp: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i]
      })),
      All: Array.from({
        length: 24
      }, (_, i) => ({
        time: `M${i + 1}`,
        value: 2.0 + Math.random() * 3.5,
        upnl: -0.5 + Math.random() * 1.5,
        timestamp: `Month ${i + 1}`
      }))
    },
    futures: {
      '1W': Array.from({
        length: 7
      }, (_, i) => ({
        time: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
        value: 0,
        timestamp: `Day ${i + 1}`
      }))
    },
    earn: {
      '1W': Array.from({
        length: 7
      }, (_, i) => ({
        time: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
        value: 3200 + i * 8,
        timestamp: `Day ${i + 1}`
      }))
    }
  };
  return baseData[accountType as keyof typeof baseData] || baseData.main;
};
const CustomTooltip = ({
  active,
  payload
}: any) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0].payload;
  return <div style={{
    background: 'rgba(20, 22, 32, 0.95)',
    backdropFilter: 'blur(32px) saturate(180%)',
    WebkitBackdropFilter: 'blur(32px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '12px',
    padding: '12px 16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
  }} data-id="element-1184">
      <div style={{
      color: '#9CA3AF',
      fontSize: '10px',
      marginBottom: '6px',
      letterSpacing: '0.05em',
      fontWeight: '400',
      textTransform: 'uppercase'
    }} data-id="element-1185">
        {data.timestamp}
      </div>

      <div style={{
      color: '#FFFFFF',
      fontSize: '18px',
      fontWeight: '500',
      letterSpacing: '-0.01em'
    }} data-id="element-1186">
        {data.value.toFixed(2)} CHF
      </div>

      {data.upnl !== undefined && <div style={{
      color: data.upnl >= 0 ? '#00C076' : '#E85D75',
      fontSize: '12px',
      marginTop: '4px'
    }} data-id="element-1187">
          UP&L: {data.upnl >= 0 ? '+' : ''}
          {data.upnl.toFixed(5)} CHF
        </div>}
    </div>;
};
export function PortfolioChart({
  accountType
}: PortfolioChartProps) {
  const [timeRange, setTimeRange] = useState<'1W' | '1M' | '3M' | '6M' | '1Y' | 'All'>('1W');
  const [chartType, setChartType] = useState<'value' | 'pnl' | 'portfolio'>('portfolio');
  const chartData = useMemo(() => {
    const data = generateChartData(accountType);
    return data[timeRange] || data['1W'];
  }, [accountType, timeRange]);
  const ranges: Array<'1W' | '1M' | '3M' | '6M' | '1Y' | 'All'> = ['1W', '1M', '3M', '6M', '1Y', 'All'];
  const getTotalValue = () => {
    if (chartData.length === 0) return 0;
    return chartData[chartData.length - 1].value;
  };
  const getChange = () => {
    if (chartData.length < 2) return {
      amount: 0,
      percent: 0
    };
    const first = chartData[0].value;
    const last = chartData[chartData.length - 1].value;
    const amount = last - first;
    const percent = amount / first * 100;
    return {
      amount,
      percent
    };
  };
  const change = getChange();
  return <div className="bg-background-secondary border border-border-subtle rounded-[20px] p-6" data-id="element-1188">
      {/* Header */}
      <div className="flex items-start justify-between mb-6" data-id="element-1189">
        <div data-id="element-1190">
          <div className="text-sm text-text-secondary mb-1 font-label uppercase" data-id="element-1191">
            Total value
          </div>
          <div className="flex items-baseline gap-2 mb-1" data-id="element-1192">
            <h2 className="text-3xl font-light text-text-primary tracking-tight font-numbers" data-id="element-1193">
              {getTotalValue().toFixed(2)}
            </h2>
            <span className="text-lg text-text-secondary font-normal" data-id="element-1194">CHF</span>
          </div>
          <span className={`flex items-center gap-1 text-sm font-medium ${change.amount >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-1195">
            {change.amount >= 0 ? '+' : ''}
            {change.amount.toFixed(2)} CHF ({change.amount >= 0 ? '+' : ''}
            {change.percent.toFixed(2)}%){' '}
            <span className="text-text-secondary" data-id="element-1196">last {timeRange}</span>
          </span>
        </div>

        <button className="p-2 hover:bg-background-elevated rounded-lg transition-colors" data-id="element-1197">
          <Settings size={18} className="text-text-secondary" data-id="element-1198" />
        </button>
      </div>

      {/* Chart Type Toggles */}
      {accountType === 'main' && <div className="flex items-center gap-2 mb-4" data-id="element-1199">
          <button onClick={() => setChartType('value')} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${chartType === 'value' ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1200">
            Value
          </button>
          <button onClick={() => setChartType('pnl')} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${chartType === 'pnl' ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1201">
            P&L
          </button>
          <button onClick={() => setChartType('portfolio')} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${chartType === 'portfolio' ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1202">
            Portfolio value
          </button>
        </div>}

      {/* Time Range Selector */}
      <div className="flex items-center gap-2 mb-6" data-id="element-1203">
        {ranges.map(range => <button key={range} onClick={() => setTimeRange(range)} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${timeRange === range ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1204">
            {range}
          </button>)}
      </div>

      {/* Chart */}
      <div className="relative w-full h-[300px]" data-id="element-1205">
        <ResponsiveContainer width="100%" height="100%" data-id="element-1206">
          {chartType === 'portfolio' && accountType === 'main' ? <ComposedChart data={chartData} margin={{
          left: 0,
          right: 0,
          top: 10,
          bottom: 0
        }} data-id="element-1207">
              <defs data-id="element-1208">
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1" data-id="element-1209">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.2} data-id="element-1210" />
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} data-id="element-1211" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2B3139" vertical={false} opacity={0.3} data-id="element-1212" />
              <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
            fill: '#848E9C',
            fontSize: 11
          }} dy={10} data-id="element-1213" />
              <YAxis hide domain={['dataMin - 0.5', 'dataMax + 0.5']} data-id="element-1214" />
              <Tooltip content={<CustomTooltip data-id="element-1216" />} cursor={{
            stroke: '#8B5CF6',
            strokeWidth: 1,
            strokeDasharray: '4 4'
          }} data-id="element-1215" />
              <Area type="monotone" dataKey="value" stroke="#8B5CF6" strokeWidth={2} fillOpacity={1} fill="url(#colorValue)" data-id="element-1217" />
              <Line type="monotone" dataKey="upnl" stroke="#E85D75" strokeWidth={1.5} dot={false} strokeDasharray="5 5" data-id="element-1218" />
            </ComposedChart> : <AreaChart data={chartData} margin={{
          left: 0,
          right: 0,
          top: 10,
          bottom: 0
        }} data-id="element-1219">
              <defs data-id="element-1220">
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1" data-id="element-1221">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.2} data-id="element-1222" />
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} data-id="element-1223" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2B3139" vertical={false} opacity={0.3} data-id="element-1224" />
              <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
            fill: '#848E9C',
            fontSize: 11
          }} dy={10} data-id="element-1225" />
              <YAxis hide domain={['dataMin - 0.5', 'dataMax + 0.5']} data-id="element-1226" />
              <Tooltip content={<CustomTooltip data-id="element-1228" />} cursor={{
            stroke: '#8B5CF6',
            strokeWidth: 1,
            strokeDasharray: '4 4'
          }} data-id="element-1227" />
              <Area type="monotone" dataKey="value" stroke="#8B5CF6" strokeWidth={2} fillOpacity={1} fill="url(#colorValue)" data-id="element-1229" />
            </AreaChart>}
        </ResponsiveContainer>
      </div>

      {/* Chart Legend for Portfolio view */}
      {chartType === 'portfolio' && accountType === 'main' && <div className="flex items-center gap-4 mt-4 text-xs" data-id="element-1230">
          <div className="flex items-center gap-2" data-id="element-1231">
            <div className="w-3 h-3 rounded-full bg-brand-purple" data-id="element-1232"></div>
            <span className="text-text-secondary" data-id="element-1233">Margin UP&L</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-1234">
            <div className="w-3 h-0.5 bg-danger" data-id="element-1235"></div>
            <span className="text-text-secondary" data-id="element-1236">Positions UP&L</span>
          </div>
        </div>}
    </div>;
}